import { useEffect, useId, useState } from 'react';
import { WORD_STATUSES, type WordStatus } from '@en3/shared';
import { Select } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

/**
 * 生词本筛选栏：状态 / 拼写搜索（防抖 300ms）/ 排序 / 只看今天到期 / 标签。
 *
 * 三个决定：
 * 1. **筛选条件由父组件持有（受控）**：`WordbookPage` 必须用同一份值同时拼请求参数与
 *    `queryKeys.wordbook(...)`。如果条件藏在子组件里，就会出现"输入框变了、query key 没变"
 *    的缓存错位 —— 表现是"筛选了但列表没变"，且很难查。
 * 2. **只对文本搜索防抖**：下拉与开关是离散操作，立即生效才符合预期。防抖放在离输入框最近的
 *    地方，用户看到的始终是自己敲的字，延迟只体现在结果上，不会有"打字卡"的感觉。
 * 3. 中文标签集中在 `WORD_STATUS_LABELS`（本文件导出，列表行与编辑弹窗复用），
 *    枚举将来增加时不会出现"筛选器里有、列表行上没有"的漏改。
 */

/** 生词本状态的统一中文标签。导出给列表行与编辑弹窗复用，保证三处文案一致。 */
export const WORD_STATUS_LABELS: Record<WordStatus, string> = {
  new: '新词',
  fuzzy: '模糊',
  unknown: '不认识',
  known: '已认识',
  mastered: '已掌握',
};

/** 排序选项。值与后端 `wordbookQuerySchema` 的枚举一一对应。 */
export type WordbookSort = 'recent' | 'due' | 'familiarity' | 'alpha';

const SORT_OPTIONS: readonly { value: WordbookSort; label: string }[] = [
  { value: 'recent', label: '最近加入' },
  { value: 'due', label: '按到期时间' },
  { value: 'familiarity', label: '按熟悉度' },
  { value: 'alpha', label: '按字母' },
];

/** 筛选条件的形状（父组件持有，见文件头第 1 条）。 */
export interface WordbookFilterValue {
  /** 拼写搜索关键词（防抖后才进请求） */
  q: string;
  /** 'all' 表示不筛状态 */
  status: WordStatus | 'all';
  sort: WordbookSort;
  /** 空串表示不筛标签 */
  tag: string;
  dueOnly: boolean;
}

/** 默认（无筛选）状态。导出它，父组件的"清空筛选"与"是否处于默认态"判断共用一份。 */
export const DEFAULT_WORDBOOK_FILTERS: WordbookFilterValue = {
  q: '',
  status: 'all',
  sort: 'recent',
  tag: '',
  dueOnly: false,
};

export function WordbookFilters({
  value,
  onChange,
  tags,
  disabled = false,
}: {
  value: WordbookFilterValue;
  /** 只传变化的字段：父组件按 patch 合并，避免子组件重复构造整个对象 */
  onChange: (patch: Partial<WordbookFilterValue>) => void;
  /** 可选的标签候选（来自当前页数据；没有标签字典端点） */
  tags: string[];
  disabled?: boolean;
}) {
  const searchId = useId();
  const statusId = useId();
  const sortId = useId();
  const tagId = useId();
  const dueId = useId();

  // 输入框的本地值：防抖期间用户看到的必须是自己敲的字。
  //
  // 这里**刻意没有**「外部 q 变了就 setText」的同步 effect：
  // 全站只有本组件会改 `value.q`，而父组件在"清空筛选"时用 `key={resetToken}`
  // 把整个筛选栏重新挂载了（见 WordbookPage），本地文本会跟着回到初始值。
  // 也就是说，那个 effect 想解决的问题已经被重挂载解决了；
  // 留着它反而会在每次父级 q 变化时多一轮渲染（React 的 set-state-in-effect 警告）。
  const [text, setText] = useState(value.q);

  const pending = text.trim();

  useEffect(() => {
    if (pending === value.q) return; // 已经一致：不再触发一次"无变化"的筛选
    const timer = window.setTimeout(() => {
      onChange({ q: pending });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [pending, value.q, onChange]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Field id={searchId} label="搜索拼写">
        <Input
          id={searchId}
          type="search"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="例如 stud"
          autoComplete="off"
          enterKeyHint="search"
          disabled={disabled}
        />
      </Field>

      <Field id={statusId} label="状态">
        <Select
          id={statusId}
          value={value.status}
          disabled={disabled}
          onChange={(event) => onChange({ status: event.target.value as WordStatus | 'all' })}
        >
          <option value="all">全部</option>
          {/* 顺序直接来自 WORD_STATUSES：枚举变了这里跟着变，不会漏 */}
          {WORD_STATUSES.map((status) => (
            <option key={status} value={status}>
              {WORD_STATUS_LABELS[status]}
            </option>
          ))}
        </Select>
      </Field>

      <Field id={sortId} label="排序">
        <Select
          id={sortId}
          value={value.sort}
          disabled={disabled}
          onChange={(event) => onChange({ sort: event.target.value as WordbookSort })}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      <div className="space-y-1.5">
        {tags.length > 0 && (
          <Field id={tagId} label="标签">
            <Select
              id={tagId}
              value={value.tag}
              disabled={disabled}
              onChange={(event) => onChange({ tag: event.target.value })}
            >
              <option value="">全部标签</option>
              {tags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="flex items-center gap-2 pt-1">
          <input
            id={dueId}
            type="checkbox"
            checked={value.dueOnly}
            disabled={disabled}
            onChange={(event) => onChange({ dueOnly: event.target.checked })}
            className="size-4 accent-primary"
          />
          <label htmlFor={dueId} className="text-sm">
            只看今天到期
          </label>
        </div>
      </div>
    </div>
  );
}
